import { ArrowRightIcon, ArrowsRightLeftIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { Fragment } from 'react';
import { Link } from 'react-router';
import hlxBrochure from '~/assets/landing/healthcare-labor-exchange-brochure.pdf?url';
import hlxBrand from '~/assets/landing/hlx-brand.webp';
import hlxTeam from '~/assets/landing/hlx-care-team.webp';
import mcvBrand from '~/assets/landing/mcv-brand.webp';
import mcvProvider from '~/assets/landing/mcv-care-provider.webp';
import mcvBrochure from '~/assets/landing/medcredvault-brochure.pdf?url';
import { useBrandConfig } from '~/config/branding';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import { buildBrandSwitchUrl, OTHER_BRAND } from '~/routing/brandSwitch';
import { paths } from '~/routing/routes';
import { HlxSections } from './HlxSections';
import { HOME_CONTENT, LANDING_DETAILS } from './homeContent';
import './landing.css';
import { McvSections } from './McvSections';

export const AccountLink = ({ hero = false }: { hero?: boolean }) => (
  <Link
    to={paths.publicAuth.loginOrCreateAccount}
    className={`landing-account-link${hero ? ' landing-account-link--hero' : ''}`}
    data-cy={hero ? CT.authentication.signInButton : undefined}
  >
    {hero ? 'Log in or create account' : 'Create Your Account'}
    {!hero && <ArrowRightIcon aria-hidden="true" />}
  </Link>
);

export const LandingPage = () => {
  const { brand } = useBrandConfig();
  const otherBrand = OTHER_BRAND[brand];
  // [MCV-1880/1881] The OTHER brand's logo opens that brand's public site, on the same env host
  // (dev/staging/prod). Same tab and signed-out aware, like the header's "Switch to" link.
  const otherBrandHref = buildBrandSwitchUrl(otherBrand, undefined, { sessionAware: true });
  const showHeroPhoto = useMediaQuery('(min-width: 768px)');
  const content = HOME_CONTENT[brand];
  const details = LANDING_DETAILS[brand];

  return (
    <main className={`landing-page landing-page--${brand}`}>
      <section className="landing-hero" aria-labelledby="landing-headline">
        <img
          className="landing-hero-brand"
          src={brand === 'mcv' ? mcvBrand : hlxBrand}
          alt=""
          width="799"
          height="613"
        />
        <div className="landing-hero-copy">
          <h1 id="landing-headline">
            {content.headline.map((line, index) => (
              <Fragment key={line}>
                {index > 0 && <br />}
                {line}
              </Fragment>
            ))}
          </h1>
          <p className="landing-subheadline">{content.subheadline}</p>
          <p className="landing-description">{content.description}</p>
          <AccountLink hero />
        </div>
        {showHeroPhoto && (
          <div className="landing-hero-photo-frame">
            <img
              className="landing-hero-photo"
              src={brand === 'mcv' ? mcvProvider : hlxTeam}
              alt=""
              width="800"
              height={brand === 'mcv' ? 533 : 531}
              fetchPriority="high"
            />
          </div>
        )}
      </section>

      <div className="landing-benefits landing-container">
        {details.benefits.map(({ title, description, icon: Icon }) => (
          <div className="landing-benefit" key={title}>
            <Icon aria-hidden="true" />
            <h2>{title}</h2>
            <p>{description}</p>
          </div>
        ))}
      </div>

      <section
        className="landing-categories landing-container"
        aria-labelledby="landing-categories-title"
      >
        <h2 id="landing-categories-title">{details.categoriesTitle}</h2>
        <div className="landing-category-grid">
          {details.categories.map(({ title, description, icon: Icon }) => (
            <div className="landing-category" key={title}>
              <Icon aria-hidden="true" />
              <div>
                <h3>{title}</h3>
                <p>{description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="landing-join" aria-labelledby="landing-join-title">
        <div className="landing-container landing-join-inner">
          <UserGroupIcon aria-hidden="true" />
          <div>
            <h2 id="landing-join-title">{details.joinTitle}</h2>
            <p>{details.joinDescription}</p>
          </div>
          <AccountLink />
        </div>
      </section>

      {brand === 'mcv' ? <McvSections /> : <HlxSections />}

      <section className="landing-connection" aria-label="Connected platforms">
        <div className="landing-container">
          <div className="landing-connection-row">
            <div className="landing-connection-links">
              <Link to="/contact">Contact Us</Link>
              {/* [MCV-1880/1881] Opens this brand's brochure PDF in a new tab. Vite's `?url` import
                  hashes the file name, so a replaced brochure is never served stale from cache. */}
              <a
                href={brand === 'mcv' ? mcvBrochure : hlxBrochure}
                target="_blank"
                rel="noopener noreferrer"
              >
                View brochure
              </a>
            </div>
            <div className="landing-connection-brands">
              <img
                src={brand === 'mcv' ? mcvBrand : hlxBrand}
                alt={brand === 'mcv' ? 'MedCredVault' : 'Healthcare Labor Exchange'}
                width="799"
                height="613"
                loading="lazy"
              />
              <ArrowsRightLeftIcon aria-hidden="true" />
              <a href={otherBrandHref}>
                <img
                  src={brand === 'mcv' ? hlxBrand : mcvBrand}
                  alt={brand === 'mcv' ? 'Visit Healthcare Labor Exchange' : 'Visit MedCredVault'}
                  width="799"
                  height="613"
                  loading="lazy"
                />
              </a>
            </div>
          </div>
          <p>
            {brand === 'mcv'
              ? 'MedCredVault connects seamlessly with Healthcare Labor Exchange'
              : 'Healthcare Labor Exchange connects seamlessly with MedCredVault'}
            , bringing professional profiles and workforce opportunities together.
          </p>
        </div>
      </section>
    </main>
  );
};

export default LandingPage;
